import { z } from 'zod'
import { moneySchema, optionalText, pageQuerySchema, versionSchema } from './common.js'
import type { TreasuryKind } from './finance.js'
import { money } from './money.js'

/**
 * Bank reconciliation. A statement line's amount is signed: positive = money into the bank (deposit,
 * bank credit), negative = money out. A journal line on the treasury account's GL account counts as
 * debit − credit. A match balances when Σ statement line amounts = Σ journal line (debit − credit).
 */

/** Most lines one statement can hold (the web parses the CSV and posts JSON). */
export const BANK_STATEMENT_MAX_LINES = 2000

export const bankStatementStatusSchema = z.enum(['IN_PROGRESS', 'COMPLETED'])
export type BankStatementStatus = z.infer<typeof bankStatementStatusSchema>

export const bankMatchKindSchema = z.enum(['MATCH', 'ADJUSTMENT'])
export type BankMatchKind = z.infer<typeof bankMatchKindSchema>

export const bankStatementLineInputSchema = z.object({
  txnDate: z.iso.date(),
  description: optionalText(250),
  reference: optionalText(100),
  amount: moneySchema.refine((v) => !money(v).isZero(), 'A line amount cannot be zero'),
})
export type BankStatementLineInput = z.input<typeof bankStatementLineInputSchema>

const periodOk = {
  check: (v: { periodStart: string; periodEnd: string }) => v.periodEnd >= v.periodStart,
  issue: { path: ['periodEnd'], message: 'The period cannot end before it starts' },
}

const headerFields = {
  /** The bank's own statement number. */
  reference: optionalText(100),
  periodStart: z.iso.date(),
  periodEnd: z.iso.date(),
  openingBalance: moneySchema,
  closingBalance: moneySchema,
}

/** Lines must be dated inside the statement period. */
function linesInPeriod(
  v: { periodStart: string; periodEnd: string; lines: { txnDate: string }[] },
  ctx: z.RefinementCtx,
) {
  v.lines.forEach((l, i) => {
    if (l.txnDate < v.periodStart || l.txnDate > v.periodEnd) {
      ctx.addIssue({
        code: 'custom',
        path: ['lines', i, 'txnDate'],
        message: 'The date is outside the statement period',
      })
    }
  })
}

export const bankStatementCreateSchema = z
  .object({
    treasuryAccountId: z.uuid(),
    ...headerFields,
    lines: z.array(bankStatementLineInputSchema).max(BANK_STATEMENT_MAX_LINES).default([]),
  })
  .refine(periodOk.check, periodOk.issue)
  .superRefine(linesInPeriod)
export type BankStatementCreate = z.input<typeof bankStatementCreateSchema>

export const bankStatementUpdateSchema = z
  .object({ ...headerFields, version: versionSchema })
  .refine(periodOk.check, periodOk.issue)
export type BankStatementUpdate = z.input<typeof bankStatementUpdateSchema>

export const bankStatementLinesAddSchema = z.object({
  lines: z.array(bankStatementLineInputSchema).min(1, 'Add at least one line').max(BANK_STATEMENT_MAX_LINES),
})
export type BankStatementLinesAdd = z.input<typeof bankStatementLinesAddSchema>

const distinct = (ids: string[]) => new Set(ids).size === ids.length

/**
 * A manual match: one or more statement lines against zero or more book lines (1:1, 1:n, n:1, n:m).
 * With no book lines, the statement lines must cancel out (a bank error and its correction).
 */
export const bankMatchInputSchema = z.object({
  statementLineIds: z
    .array(z.uuid())
    .min(1, 'Choose at least one statement line')
    .max(BANK_STATEMENT_MAX_LINES)
    .refine(distinct, 'A line is listed twice'),
  journalLineIds: z
    .array(z.uuid())
    .max(BANK_STATEMENT_MAX_LINES)
    .default([])
    .refine(distinct, 'A line is listed twice'),
})
export type BankMatchInput = z.input<typeof bankMatchInputSchema>

export const bankAutoMatchSchema = z.object({
  /** Days a book line's date may differ from the statement line's date. */
  windowDays: z.number().int().min(0).max(10).default(3),
})
export type BankAutoMatchInput = z.input<typeof bankAutoMatchSchema>

export interface BankAutoMatchResult {
  /** Pairs matched by this run. */
  matched: number
  /** Statement lines still unmatched afterwards. */
  unmatched: number
}

/** Posts the statement line to `accountId` (bank charges, interest) and matches the new entry. */
export const bankAdjustmentSchema = z.object({
  accountId: z.uuid(),
  description: z.string().trim().min(1, 'Describe the adjustment').max(300),
})
export type BankAdjustmentInput = z.input<typeof bankAdjustmentSchema>

export const bankStatementReopenSchema = z.object({
  reason: z.string().trim().min(1, 'Give a reason').max(300),
})
export type BankStatementReopenInput = z.input<typeof bankStatementReopenSchema>

export const bankStatementListQuerySchema = pageQuerySchema.extend({
  treasuryAccountId: z.uuid().optional(),
  status: bankStatementStatusSchema.optional(),
  /** Statements whose period ends on or after this date. */
  from: z.iso.date().optional(),
  /** Statements whose period starts on or before this date. */
  to: z.iso.date().optional(),
})
export type BankStatementListQuery = z.input<typeof bankStatementListQuerySchema>

/** `GET /bank-statements/summary`: the list's filters other than `status`, paging and sort. */
export const bankStatementSummaryQuerySchema = bankStatementListQuerySchema.pick({
  q: true,
  treasuryAccountId: true,
  from: true,
  to: true,
})
export type BankStatementSummaryQuery = z.input<typeof bankStatementSummaryQuerySchema>

/**
 * The statement list's figures for the current filters. `all`, `inProgress` and `completed` each
 * equal the `total` of the list call with that status (none, IN_PROGRESS, COMPLETED).
 * `inProgress.lineCount` / `matchedLineCount` sum those statements' lines. `unmatched`: the statement
 * lines still to match (only statements in progress have any), with the money in (positive lines) and
 * out (negative lines, as a positive sum). `completed.reconciledThrough`: the latest period end among
 * the completed statements (null when none).
 */
export interface BankStatementListSummary {
  all: { count: number }
  inProgress: { count: number; lineCount: number; matchedLineCount: number }
  completed: { count: number; reconciledThrough: string | null }
  unmatched: { count: number; moneyIn: string; moneyOut: string }
}

export const bankCandidatesQuerySchema = z.object({
  /** Book lines dated within `windowDays` of this statement line (default 3 days), exact amounts first. */
  lineId: z.uuid().optional(),
  /** Without `lineId`: book lines dated within this many days of the statement period (default: all). */
  windowDays: z.coerce.number().int().min(0).max(366).optional(),
})
export type BankCandidatesQuery = z.input<typeof bankCandidatesQuerySchema>

/** A journal line on the treasury account's GL account, with the document behind it. */
export interface BankBookLine {
  journalLineId: string
  entryId: string
  entryNumber: string
  entryDate: string
  sourceType: string | null
  sourceId: string | null
  reference: string | null
  description: string | null
  /** debit − credit: positive = money into the account. */
  amount: string
}

export interface BankCandidate extends BankBookLine {
  /** With `lineId`: the amount equals the statement line's (same direction). */
  exactAmount: boolean
}

export interface BankStatementLine {
  id: string
  lineNo: number
  txnDate: string
  description: string | null
  reference: string | null
  amount: string
  matchId: string | null
}

export interface BankMatch {
  id: string
  kind: BankMatchKind
  statementLineIds: string[]
  journalLines: BankBookLine[]
  createdAt: string
}

/**
 * The reconciliation as of the statement's period end:
 *   adjusted book = book balance − outstanding deposits + outstanding payments + cleared ahead of booking
 *                   − uncleared opening items
 *   difference    = closing balance − adjusted book
 */
export interface BankReconciliationSummary {
  asOf: string
  lineCount: number
  matchedLineCount: number
  /** Σ positive statement lines. */
  deposits: string
  /** Σ negative statement lines, as a positive figure. */
  withdrawals: string
  /** closing − (opening + Σ lines); must be 0 to complete. */
  statementDifference: string
  /** Ledger balance of the account's GL account at the period end. */
  bookBalance: string
  outstandingDeposits: string
  /** A positive figure. */
  outstandingPayments: string
  /** Signed: book lines dated after the period end that the bank already cleared. */
  clearedAheadOfBooking: string
  adjustedBookBalance: string
  difference: string
  /**
   * The first statement's period start. Book lines before it are not listed as outstanding one by one
   * (history the bank cleared long ago); they can still be matched when they clear.
   */
  reconciliationStart: string
  /**
   * First statement only: book balance the day before the reconciliation start − the first opening
   * balance, i.e. the items booked before the start that the bank had not cleared yet.
   */
  startingDifference: string | null
  /**
   * Signed: that starting difference less the items booked before the start that this or an earlier
   * statement has matched since. Zero once all of them have cleared.
   */
  unclearedOpeningItems: string
}

export interface BankReconciliationReport extends BankReconciliationSummary {
  statementId: string
  number: string
  /** Book lines dated up to the period end not cleared by this or an earlier statement. */
  outstanding: BankBookLine[]
  /** Book lines dated after the period end cleared by this or an earlier statement. */
  clearedAhead: BankBookLine[]
}

export interface BankStatementListItem {
  id: string
  number: string
  treasuryAccount: { id: string; name: string; kind: TreasuryKind }
  reference: string | null
  periodStart: string
  periodEnd: string
  openingBalance: string
  closingBalance: string
  status: BankStatementStatus
  lineCount: number
  matchedLineCount: number
  completedAt: string | null
}

export interface BankStatement extends BankStatementListItem {
  completedBy: string | null
  version: number
  createdAt: string
  updatedAt: string
  lines: BankStatementLine[]
  matches: BankMatch[]
  summary: BankReconciliationSummary
}
